import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { Navigate } from 'react-router';

import LoadingIndicator from '@/components/LoadingIndicator';
import { AUTH_ERROR_STORAGE_KEY } from '@/constants/common';
import useRemoveAppSkeleton from '@/hooks/useRemoveAppSkeleton';
import type { CommonProps } from '@/types/common';

import {
  selectedOrganizationAtom,
  selectedOrganizationIdAtom,
  useFetchOrganizations
} from '../Organizations';

// This component is used to load the organizations before rendering the page, this ensures that permission are determined before rendering the page
const OrganizationLoader = ({ children }: CommonProps) => {
  const { data, error } = useFetchOrganizations();
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [selectedOrganizationId, setSelectedOrganizationId] = useAtom(
    selectedOrganizationIdAtom
  );

  const organizations = data || [];
  const isReady = organizations.length > 0 && selectedOrganization.id !== -1;

  useRemoveAppSkeleton(isReady);

  if ((error as AxiosError)?.response?.status === 403) {
    // This error happens when the user does not have the organization.read permission
    sessionStorage.setItem(
      AUTH_ERROR_STORAGE_KEY,
      "You don't have permission to access this page. Please contact your administrator."
    );
    return <Navigate to="/login" replace />;
  }

  // ensure the selected organization is correctly set
  if (
    organizations.length === 1 &&
    selectedOrganizationId !== organizations[0].id
  ) {
    setSelectedOrganizationId(organizations[0].id);
  }

  if (!isReady) {
    return <LoadingIndicator isLoading />;
  }
  return children;
};

export default OrganizationLoader;
